import React, { useState, useEffect } from "react";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { usePodData } from "../../hooks/usePodData";
import { useLocalization } from "../../context/LocalizationContext";
import { regenerateQRCode, getActiveQRCode } from "../../utils/api";
import { extractIPFromURL, getSavedPodIP, parseAsUTC } from "../../utils/helpers";
import { logSecretly } from "../../utils/logSecretly";
import { useSessionStartedListener } from "../../hooks/useSessionStartedListener";
import MockLoginButton from "../../components/MockLoginButton";
import type { ActiveQRCodeResponse } from "../../dtos/qr";
import "./styles.css";
import Header from "../../containers/Header";
import { useAuth as useOidcAuth } from "react-oidc-context";
import Button from "../../components/shared/Button";
import { useAuth } from "../../context/AuthContext";
import { tokenStorage } from "../../utils/tokenStorage";
import LoadingScreen from "../../components/LoadingScreen";

const Welcome: React.FC = () => {
  const navigate = useAppNavigate();
  const { podId } = usePodData();
  const { t } = useLocalization();
  const [qrData, setQrData] = useState<ActiveQRCodeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);


  const auth = useOidcAuth();
  const { handleAuthentication, handleStartSession, isLoading } = useAuth();


  // Listen for SESSION_STARTED via WebSocket
  useSessionStartedListener();

  const regenerateQR = async () => {
    if (!podId) return;
    try {
      setLoading(true);
      setError(null);
      setIsExpired(false);
      const data = await regenerateQRCode(podId);
      setQrData(data);
    } catch (err) {
      logSecretly("Failed to fetch QR code:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  // Fetch QR code on mount
  useEffect(() => {
    if (!podId) return;
    let cancelled = false;

    const fetchQR = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsExpired(false);
        const data = await getActiveQRCode(podId);
        if (!cancelled) setQrData(data);
      } catch (err) {
        if (!cancelled) {
          logSecretly("Failed to fetch QR code:", err);
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchQR();
    return () => { cancelled = true; };
  }, [podId]);


  // Set a timeout to mark the QR code as expired based on expiresAt
  useEffect(() => {
    if (!qrData?.expiresAt) return;

    const expiresMs = parseAsUTC(qrData.expiresAt);
    const now = Date.now();
    const SAFETY_BUFFER_MS = 60000; // 1 minute
    const timeUntilExpiry = (expiresMs - now) - SAFETY_BUFFER_MS;

    if (timeUntilExpiry <= 0) {
      setIsExpired(true);
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsExpired(true);
    }, timeUntilExpiry);

    return () => clearTimeout(timeoutId);
  }, [qrData?.expiresAt]);

  useEffect(() => {
    const savedPodIp = extractIPFromURL(window.location.href);
    const activeSession = tokenStorage.getPodSession()?.sessionId;

    if (auth.isAuthenticated && savedPodIp && !isLoading) {
      setLoading(true);
      !activeSession && handleStartSession(savedPodIp).then(() => {
        navigate("/dashboard")
      }).finally(() => setLoading(false));
    }
  }, [auth.isAuthenticated]);

  return (loading || isLoading) ? <LoadingScreen /> : (
    <div className="welcome-screen page-container">
      <Header showSettingsButton={true} />

      {/* Main Content */}
      <div className="welcome-main-content">
        <h1 className="welcome-main-title page-title">{t("welcomeScreen.title")}</h1>
        <p className="welcome-subtitle page-subtitle">{t("welcomeScreen.subtitle")}</p>

        {loading && (
          <div className="qr-loading">
            <p>{t("loading")}</p>
          </div>
        )}
        {error && (
          <div className="qr-error">
            <p>{t("error")}: {error}</p>
          </div>
        )}
        {/* QR Code Container */}
        {!loading && !error && (
          <>
            <div className={`qr-code-container ${isExpired ? 'qr-code-expired' : ''}`}>
              {qrData && qrData.qrCodeBase64 && (
                <img
                  src={`data:image/png;base64,${qrData.qrCodeBase64}`}
                  alt="QR Code"
                  className={`qr-code-image ${isExpired ? 'qr-image-blurred' : ''}`}
                />
              )}

            </div>
            {isExpired && (
              <button className="qr-refetch-button" onClick={regenerateQR}>
                {t("welcomeScreen.refetch")}
              </button>
            )}
          </>
        )}

      </div>

      {/* Bottom Buttons */}
      <div className="welcome-bottom-actions">
        <MockLoginButton qrToken={qrData?.qrToken} />
        {auth.isAuthenticated &&
          <button className="welcome-button" onClick={() => navigate("/dashboard")}>{t('start_journey')}</button>

        }
        {auth.isAuthenticated ?
          <button className="welcome-button secondary" onClick={() => auth.signoutRedirect()}>{t('logout')}</button> :
          <button className="welcome-button" onClick={handleAuthentication}>{t('login')}</button>}
        {/* <button
          className="welcome-button secondary"
          onClick={() => navigate("/instructions")}
        >
          {t("welcomeScreen.viewInstructions")}
        </button> */}
      </div>
    </div>
  );
};

export default Welcome;
